import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { tokens } from '../../theme';
import {
  adminTokens,
  formatCompactDate,
  formatRelativeTimeLong,
  panelSx,
} from './adminTokens';

const { border: BORDER, editorialSerif } = adminTokens;

function WaitlistStatusBadge({ status }) {
  const palette = {
    active: { fg: '#dff2e4', bg: 'rgba(123,196,142,0.22)', border: 'rgba(123,196,142,0.45)' },
    exported: { fg: '#f4e2cf', bg: 'rgba(212,144,99,0.22)', border: 'rgba(212,144,99,0.45)' },
    unsubscribed: { fg: '#f7d5d1', bg: 'rgba(230,124,115,0.22)', border: 'rgba(230,124,115,0.45)' },
  };
  const style = palette[status] || palette.active;
  return (
    <Chip
      label={status || 'active'}
      size="small"
      sx={{
        height: 20,
        borderRadius: '2px',
        fontSize: '0.64rem',
        textTransform: 'capitalize',
        color: style.fg,
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
      }}
    />
  );
}

function MetadataRow({ label, value }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1.5, py: 0.5, alignItems: 'baseline' }}>
      <Typography variant="caption" sx={{ color: tokens.textMuted }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: tokens.cream }}>{value || '—'}</Typography>
    </Box>
  );
}

function matchesSubmissionFilter(count, filter) {
  if (filter === 'repeat') return count >= 2;
  if (filter === '5plus') return count >= 5;
  if (filter === '1') return count === 1;
  return true;
}

export default function AdminWaitlistOperationsView({
  entries,
  loading,
  error,
  search,
  statusFilter,
  onStatusFilterChange,
  placementFilter,
  onPlacementFilterChange,
  sourceFilter,
  onSourceFilterChange,
  submissionFilter,
  onSubmissionFilterChange,
  selectedEntryId,
  onSelectEntry,
  onExport,
  exporting,
}) {
  const placementOptions = useMemo(() => {
    const values = new Set();
    entries.forEach((entry) => (entry.placements || []).forEach((placement) => values.add(placement)));
    return Array.from(values).sort();
  }, [entries]);

  const sourceOptions = useMemo(() => {
    const values = new Set(entries.map((entry) => entry.source).filter(Boolean));
    return Array.from(values).sort();
  }, [entries]);

  const filteredEntries = useMemo(() => {
    const term = search.trim().toLowerCase();
    return entries
      .filter((entry) => {
        if (statusFilter !== 'all' && entry.status !== statusFilter) return false;
        if (placementFilter !== 'all' && !(entry.placements || []).includes(placementFilter)) return false;
        if (sourceFilter !== 'all' && entry.source !== sourceFilter) return false;
        if (!matchesSubmissionFilter(entry.submissionCount || 1, submissionFilter)) return false;
        if (!term) return true;

        return [
          entry.email,
          entry.source,
          entry.status,
          ...(entry.placements || []),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      })
      .sort((a, b) => new Date(b.lastJoinedAt || b.createdAt || 0) - new Date(a.lastJoinedAt || a.createdAt || 0));
  }, [entries, search, statusFilter, placementFilter, sourceFilter, submissionFilter]);

  useEffect(() => {
    if (loading) return;
    if (!filteredEntries.length) {
      if (selectedEntryId) onSelectEntry(null);
      return;
    }
    if (!selectedEntryId || !filteredEntries.some((entry) => entry.id === selectedEntryId)) {
      onSelectEntry(filteredEntries[0].id);
    }
  }, [filteredEntries, loading, onSelectEntry, selectedEntryId]);

  const selectedEntry = filteredEntries.find((entry) => entry.id === selectedEntryId)
    || entries.find((entry) => entry.id === selectedEntryId)
    || null;

  const repeatedEntries = filteredEntries.filter((entry) => (entry.submissionCount || 1) >= 2).length;
  const activeEntries = filteredEntries.filter((entry) => entry.status === 'active').length;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={panelSx({ p: 0, width: 360, flexShrink: 0, minHeight: 0, display: 'flex', flexDirection: 'column' })}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>Waitlist Ops</Typography>
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                Filtered by the workspace search bar plus source, placement, and repeat intent.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              disabled={loading || exporting}
              onClick={onExport}
            >
              {exporting ? 'Exporting…' : 'CSV export'}
            </Button>
          </Stack>

          <Stack direction="row" spacing={0.75} sx={{ mb: 1.25 }} useFlexGap flexWrap="wrap">
            <Chip label={`${filteredEntries.length} shown`} size="small" sx={{ borderRadius: 0 }} />
            <Chip label={`${activeEntries} active`} size="small" sx={{ borderRadius: 0 }} />
            <Chip label={`${repeatedEntries} repeat joins`} size="small" sx={{ borderRadius: 0 }} />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.9,
            }}
          >
            <Select size="small" value={statusFilter} onChange={(event) => onStatusFilterChange(event.target.value)} sx={{ borderRadius: 0, fontSize: '0.8rem' }}>
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="exported">Exported</MenuItem>
              <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
            </Select>
            <Select size="small" value={submissionFilter} onChange={(event) => onSubmissionFilterChange(event.target.value)} sx={{ borderRadius: 0, fontSize: '0.8rem' }}>
              <MenuItem value="all">All submission counts</MenuItem>
              <MenuItem value="1">Single submission</MenuItem>
              <MenuItem value="repeat">Repeat joins (2+)</MenuItem>
              <MenuItem value="5plus">Heavy repeat (5+)</MenuItem>
            </Select>
            <Select size="small" value={placementFilter} onChange={(event) => onPlacementFilterChange(event.target.value)} sx={{ borderRadius: 0, fontSize: '0.8rem' }}>
              <MenuItem value="all">All placements</MenuItem>
              {placementOptions.map((placement) => (
                <MenuItem key={placement} value={placement}>{placement}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={sourceFilter} onChange={(event) => onSourceFilterChange(event.target.value)} sx={{ borderRadius: 0, fontSize: '0.8rem' }}>
              <MenuItem value="all">All sources</MenuItem>
              {sourceOptions.map((source) => (
                <MenuItem key={source} value={source}>{source}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: tokens.textMuted }}>Loading waitlist entries…</Typography>
            </Box>
          )}
          {!loading && !filteredEntries.length && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                {error || 'No waitlist entries match the current filters.'}
              </Typography>
            </Box>
          )}
          {!loading && filteredEntries.map((entry) => (
            <Box
              key={entry.id}
              onClick={() => onSelectEntry(entry.id)}
              sx={{
                p: 1.6,
                cursor: 'pointer',
                borderBottom: `1px solid ${BORDER}`,
                bgcolor: entry.id === selectedEntryId ? 'rgba(169,191,122,0.08)' : 'transparent',
                '&:hover': { bgcolor: entry.id === selectedEntryId ? 'rgba(169,191,122,0.1)' : 'rgba(255,255,255,0.03)' },
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.7 }}>
                <Typography variant="body2" sx={{ color: tokens.cream, fontWeight: 500 }} noWrap>
                  {entry.email}
                </Typography>
                <WaitlistStatusBadge status={entry.status} />
              </Stack>
              <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.6 }}>
                {entry.source || 'website'} · {formatRelativeTimeLong(entry.lastJoinedAt || entry.createdAt)}
              </Typography>
              <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mb: 0.75 }}>
                {(entry.placements || []).slice(0, 3).map((placement) => (
                  <Chip key={placement} label={placement} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem' }} />
                ))}
              </Stack>
              <Typography variant="caption" sx={{ color: tokens.textSoft }}>
                {entry.submissionCount || 1} submission{(entry.submissionCount || 1) === 1 ? '' : 's'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={panelSx({ p: 0, flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' })}>
        {selectedEntry ? (
          <>
            <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>
                      {selectedEntry.email}
                    </Typography>
                    <WaitlistStatusBadge status={selectedEntry.status} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: tokens.textSoft }}>
                    Placement and source context for early-access demand triage.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  disabled={loading || exporting}
                  onClick={onExport}
                >
                  {exporting ? 'Exporting…' : 'Export filtered CSV'}
                </Button>
              </Stack>
            </Box>

            <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>
                Acquisition Snapshot
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: '4px 28px',
                }}
              >
                <MetadataRow label="Status" value={selectedEntry.status} />
                <MetadataRow label="Source" value={selectedEntry.source} />
                <MetadataRow label="Submission count" value={String(selectedEntry.submissionCount || 1)} />
                <MetadataRow label="Placement count" value={String((selectedEntry.placements || []).length)} />
                <MetadataRow label="First joined" value={formatCompactDate(selectedEntry.firstJoinedAt || selectedEntry.createdAt)} />
                <MetadataRow label="Last joined" value={formatCompactDate(selectedEntry.lastJoinedAt || selectedEntry.updatedAt)} />
                <MetadataRow label="Created" value={formatCompactDate(selectedEntry.createdAt)} />
                <MetadataRow label="Updated" value={formatCompactDate(selectedEntry.updatedAt)} />
              </Box>
            </Box>

            <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>
                Placement Context
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2.25 }}>
                {(selectedEntry.placements || []).length ? (
                  selectedEntry.placements.map((placement) => (
                    <Chip
                      key={placement}
                      label={placement}
                      size="small"
                      sx={{ borderRadius: 0, height: 24, fontSize: '0.68rem' }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>No placement metadata captured.</Typography>
                )}
              </Stack>

              <Typography variant="subtitle2" sx={{ color: tokens.cream, mb: 1.25, fontFamily: editorialSerif }}>
                Operations Notes
              </Typography>
              <Box sx={{ p: 1.75, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Typography variant="body2" sx={{ color: tokens.textSoft, lineHeight: 1.65 }}>
                  This contact has joined from <strong>{selectedEntry.source || 'website'}</strong>
                  {selectedEntry.submissionCount > 1 ? ` and has re-entered the waitlist ${selectedEntry.submissionCount} times.` : ' with a single recorded submission.'}
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.textSoft, lineHeight: 1.65, mt: 1 }}>
                  Use placement context to understand where demand is coming from, then export a status-filtered CSV for the broader outreach workflow.
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 0.75 }}>
              Waitlist detail
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textMuted }}>
              Select an entry from the queue to inspect placement context and repeat-join behavior.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
